/**
 * Cliente HTTP de la pestaña Configuración. Vive al lado de la vista que lo
 * usa (§2.1) y solo llama a rutas que `back/src/configuracion/rutas.ts` expone.
 */

import {
  parchear,
  pedir,
  postear,
  type ConceptoRegla,
  type Feriado,
} from '../../api';

/** Calendario de un año: los que valen siempre, más los móviles de ese año. */
export async function listarFeriados(anio?: number): Promise<readonly Feriado[]> {
  const ruta =
    anio === undefined
      ? '/api/configuracion/feriados'
      : `/api/configuracion/feriados?anio=${String(anio)}`;
  const r = await pedir<{ feriados: readonly Feriado[] }>(ruta);
  return r.feriados;
}

export interface AltaFeriado {
  readonly dia: number;
  readonly mes: number;
  /** Sin año, el feriado vale para todos los años. */
  readonly anio?: number;
  readonly descripcion: string;
}

export function agregarFeriado(alta: AltaFeriado): Promise<Feriado> {
  return postear<Feriado>('/api/configuracion/feriados', alta);
}

export function borrarFeriado(clave: string): Promise<{ ok: true }> {
  return pedir<{ ok: true }>(`/api/configuracion/feriados/${encodeURIComponent(clave)}`, {
    method: 'DELETE',
  });
}

/** Reglas con su concepto de Visma, ya con lo editado desde el portal aplicado. */
export interface Diccionario {
  readonly conceptos: readonly ConceptoRegla[];
  /** False cuando la tabla de parámetros todavía no existe en la base. */
  readonly parametrosEditables: boolean;
  /** False cuando la tabla de rangos de fecha todavía no existe en la base. */
  readonly rangosEditables: boolean;
}

export async function listarConceptos(): Promise<Diccionario> {
  const r = await pedir<{
    conceptos: readonly ConceptoRegla[];
    parametrosEditables?: boolean;
    rangosEditables?: boolean;
  }>('/api/configuracion/conceptos');
  return {
    conceptos: r.conceptos,
    parametrosEditables: r.parametrosEditables !== false,
    rangosEditables: r.rangosEditables !== false,
  };
}

/** Cambia el código de Visma de una regla. Lo demás no se edita. */
export function editarConcepto(id: string, concepto: string): Promise<unknown> {
  return parchear(`/api/configuracion/conceptos/${encodeURIComponent(id)}`, { concepto });
}

/** Vuelve al código que trae el archivo de reglas. */
export function restablecerConcepto(id: string): Promise<{ ok: true }> {
  return pedir<{ ok: true }>(`/api/configuracion/conceptos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Cambia el `parameterId` de un concepto de Visma.
 *
 * Va por concepto y no por regla: del lado de Visma el parámetro es uno solo,
 * así que cambiarlo afecta a todas las reglas que mandan a ese concepto.
 */
export function editarParametro(concepto: string, parametro: number): Promise<unknown> {
  return pedir(`/api/configuracion/parametros/${encodeURIComponent(concepto)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parametro }),
  });
}

/**
 * Marca o desmarca el concepto como de los que piden un rango de fechas.
 *
 * Por concepto y no por regla: es lo que se tilda en la pantalla de novedades,
 * y dos reglas del mismo concepto no pueden pedir cosas distintas.
 */
export function editarRangoFecha(concepto: string, requiere: boolean): Promise<unknown> {
  return pedir(`/api/configuracion/rango-fecha/${encodeURIComponent(concepto)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requiere }),
  });
}

/** Vuelve a lo que diga el archivo de reglas. */
export function restablecerRangoFecha(concepto: string): Promise<unknown> {
  return pedir(`/api/configuracion/rango-fecha/${encodeURIComponent(concepto)}`, {
    method: 'DELETE',
  });
}

/** Vuelve al parámetro que trae el archivo. */
export function restablecerParametro(concepto: string): Promise<unknown> {
  return pedir(`/api/configuracion/parametros/${encodeURIComponent(concepto)}`, {
    method: 'DELETE',
  });
}
